import React from 'react';

// ChatSidebar header component with purple gradient theme, collapse functionality, and thinking animations
const ChatSidebarHeader = ({ onToggleCollapse, onReopenTutorial, onStartNewConversation, isThinking = false }) => {
  // Check if there's a stored tutorial
  const hasStoredTutorial = () => {
    try {
      const stored = localStorage.getItem('queryburst_last_tutorial');
      return !!stored;
    } catch (error) {
      return false;
    }
  };
  return (
    <div 
      className={`
        bg-gradient-to-r from-purple-600 to-pink-600 text-white relative overflow-hidden
        transition-all duration-500
        ${isThinking ? 'shadow-lg shadow-purple-500/30' : ''}
        p-2 md:p-4
      `} 
      data-chat-header
    >
      {/* Thinking glow effect overlay */}
      {isThinking && (
        <div className="absolute inset-0 bg-gradient-to-r from-purple-400 via-pink-400 to-purple-400 opacity-20 animate-pulse" 
             style={{ animationDuration: '2s' }} />
      )}
      
      {/* Thinking wave particles - hidden on mobile for cleaner look */}
      {isThinking && (
        <div className="absolute inset-0 overflow-hidden pointer-events-none hidden md:block">
          <div className="absolute top-2 left-8 w-1 h-1 bg-white bg-opacity-40 rounded-full animate-ping" 
               style={{ animationDelay: '0s', animationDuration: '3s' }} />
          <div className="absolute top-3 right-12 w-1.5 h-1.5 bg-white bg-opacity-30 rounded-full animate-ping" 
               style={{ animationDelay: '1s', animationDuration: '3s' }} />
          <div className="absolute bottom-3 left-16 w-1 h-1 bg-white bg-opacity-35 rounded-full animate-ping" 
               style={{ animationDelay: '2s', animationDuration: '3s' }} />
          <div className="absolute top-4 left-1/2 w-0.5 h-0.5 bg-white bg-opacity-40 rounded-full animate-ping" 
               style={{ animationDelay: '0.5s', animationDuration: '4s' }} />
        </div>
      )}
      
      <div className="flex items-center justify-between relative z-10">
        <div className="flex items-center gap-2 md:gap-3">
          <div className={`
            bg-white bg-opacity-20 rounded-lg flex items-center justify-center backdrop-blur-sm
            transition-all duration-500
            ${isThinking ? 'bg-opacity-30 shadow-lg scale-105 animate-pulse' : ''}
            w-6 h-6 md:w-8 md:h-8
          `} data-avatar>
            <svg className={`
              text-white transition-all duration-300
              ${isThinking ? 'animate-pulse' : ''}
              w-3 h-3 md:w-4 md:h-4
            `} fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z" clipRule="evenodd" />
            </svg>
          </div>
          
          {/* Desktop: Full title and status */}
          <div className="hidden md:block">
            <h3 className="font-bold text-sm text-white">
              SEO Consultant
            </h3>
            <div className="flex items-center gap-1">
              <div className={`
                w-2 h-2 rounded-full transition-all duration-300
                ${isThinking 
                  ? 'bg-yellow-400 animate-bounce shadow-sm shadow-yellow-400/50' 
                  : 'bg-green-400 animate-pulse'
                }
              `} style={isThinking ? { animationDuration: '1s' } : {}} />
              <span className="text-xs text-white text-opacity-90">
                {isThinking ? 'Thinking...' : 'Always Online'}
              </span>
              {isThinking && (
                <div className="flex gap-0.5 ml-1">
                  <div className="w-0.5 h-0.5 bg-white bg-opacity-60 rounded-full animate-bounce" 
                       style={{ animationDelay: '0ms', animationDuration: '1.2s' }} />
                  <div className="w-0.5 h-0.5 bg-white bg-opacity-60 rounded-full animate-bounce" 
                       style={{ animationDelay: '200ms', animationDuration: '1.2s' }} />
                  <div className="w-0.5 h-0.5 bg-white bg-opacity-60 rounded-full animate-bounce" 
                       style={{ animationDelay: '400ms', animationDuration: '1.2s' }} />
                </div>
              )}
            </div>
          </div>
          
          {/* Mobile: Compact title with inline status */}
          <div className="md:hidden flex items-center gap-2">
            <span className="font-medium text-sm text-white">SEO Consultant</span>
            <div className={`
              w-1.5 h-1.5 rounded-full transition-all duration-300
              ${isThinking 
                ? 'bg-yellow-400 animate-bounce' 
                : 'bg-green-400 animate-pulse'
              }
            `} style={isThinking ? { animationDuration: '1s' } : {}} />
          </div>
        </div>
        
        <div className="flex items-center gap-1 md:gap-3">
          
          {/* Tutorial reopen button - hidden on mobile */}
          {onReopenTutorial && hasStoredTutorial() && (
            <button
              onClick={onReopenTutorial}
              className="p-1 rounded-lg transition-colors hover:bg-white hover:bg-opacity-20 text-white hover:text-white hidden md:block"
              title="Reopen Last Tutorial"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
            </button>
          )}
          
          <button
            onClick={onToggleCollapse}
            className="p-1 rounded-lg transition-colors hover:bg-white hover:bg-opacity-20 text-white hover:text-white"
            title="Collapse SEO Assistant"
          >
            {/* Mobile: Down arrow, Desktop: Right arrow */}
            <svg className="w-4 h-4 md:hidden" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
            <svg className="w-4 h-4 hidden md:block" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChatSidebarHeader;
